<template>
  <b-row>
    <b-col sm="12">
      <b-card body-class="py-2">
        <section class="list-view-heading">
          <ModuleViewHeading
            :module-id="access.module_id"
            :profile-id="access.profile_id"
            :reference="access.view_reference"
            summary-only
          />
          <div class="list-view-heading__controls">
            <div class="list-view-heading__actions">
              <TabHeaderActions />
            </div>
            <div class="list-view-heading__controls-end">
              <b-button variant="link"
                        class="list-view-icon-btn"
                        :class="{ 'list-view-icon-btn--active': filtersOpen }"
                        v-b-tooltip.hover.bottom
                        :title="filtersOpen ? 'Ocultar filtros' : 'Mostrar filtros'"
                        :aria-label="filtersOpen ? 'Ocultar filtros' : 'Mostrar filtros'"
                        @click="filtersOpen = !filtersOpen">
                <AppIcon name="filter" :fill="filtersOpen" />
              </b-button>
              <b-dropdown right
                          no-caret
                          variant="link"
                          boundary="window"
                          toggle-class="list-view-icon-btn list-view-icon-btn--primary"
                          v-b-tooltip.hover.bottom
                          title="Agregar Remito de Compra">
                <template #button-content>
                  <AppIcon name="add" label="Agregar Remito de Compra" />
                </template>
                <b-dropdown-item @click="add()">
                  <AppIcon name="add" /> Alta remito normal
                </b-dropdown-item>
                <b-dropdown-item @click="addFromPurchaseOrder()">
                  <AppIcon name="cart" /> Alta desde orden de compra
                </b-dropdown-item>
              </b-dropdown>
            </div>
          </div>
        </section>

        <b-collapse v-model="filtersOpen">
          <div class="list-view-toolbar__filters">
            <div class="list-view-filter" style="width: 150px;">
              <label class="filter-label">Desde</label>
              <b-form-input type="date" size="sm" v-model="filters.date_start"></b-form-input>
            </div>
            <div class="list-view-filter" style="width: 150px;">
              <label class="filter-label">Hasta</label>
              <b-form-input type="date" size="sm" v-model="filters.date_end"></b-form-input>
            </div>
            <div class="list-view-filter" style="flex: 1 1 230px; max-width: 340px;">
              <label class="filter-label">Proveedores</label>
              <v-select :options="arr.filters.suppliers"
                        v-model="filters.suppliers"
                        placeholder="Todos"
                        :multiple="true"
                        :select-on-tab="true"></v-select>
            </div>
            <b-button variant="outline-dark" size="sm" @click="filterPurchaseRemits(true)" v-b-tooltip.hover title="Aplicar filtros" style="height: 31px; width: 31px; padding: 0; flex-shrink: 0;">
              <AppIcon name="filter" />
            </b-button>
          </div>
        </b-collapse>
      </b-card>

      <b-row>
        <b-col lg="12" id="view_list_purchase_remits">
          <b-card no-body>
            <b-row no-gutters>
              <b-col sm="12">
                <b-table class="app-table table-purchase-remits-custom"
                        ref="table"
                        responsive
                        :stacked="isMobileViewport ? 'md' : false"
                        sticky-header="60vh"
                        :hover="true"
                        :small="true"
                        :fixed="true"
                        :items="table.items"
                        :fields="table.fields"
                        :current-page="table.currentPage"
                        :per-page="table.perPage"
                        @row-clicked="onRowClicked"
                        :tbody-tr-class="rowClass"
                        :busy="table.isBusy"                      
                        v-if="table.items.length || table.isBusy">                       
                    
                    <template v-slot:table-colgroup="scope">
                      <col
                        v-for="field in scope.fields"                    
                        :key="field.key"
                        :style="{ width: field.width }"
                      >
                    </template> 

                    <template v-slot:table-busy>
                      <div class="text-center text-danger my-2">
                        <b-spinner class="align-middle"></b-spinner>
                        <strong>Cargando...</strong>
                      </div>
                    </template>
                    
                    <template v-slot:cell(business)="data">
                      {{data.item.points_sales.business.reference}}
                    </template>                    
                    
                    <template v-slot:cell(voucher)="data">
                      <div v-if="data.item.type_voucher && data.item.point_sale">
                        <b-badge variant="dark" v-if="data.item.points_sales">
                          {{data.item.points_sales.point_sale}} - {{data.item.points_sales.name}}
                        </b-badge> <br>
                        {{data.item.type_voucher.name}}
                        {{data.item.point_sale.toString().padStart(4,'0') + '-' + data.item.number.toString().padStart(8,'0')}}
                        <b-badge variant="success" class="ml-1"
                                 v-if="isRemitLinked(data.item)"
                                 v-b-tooltip.hover title="Todos los ítems tienen factura vinculada">
                          <AppIcon name="check2-circle" /> Factura vinculada
                        </b-badge>
                        <b-badge variant="warning" class="ml-1"
                                 v-else-if="isRemitPartiallyLinked(data.item)"
                                 v-b-tooltip.hover title="Algunos ítems aún no tienen factura. Usá 'Cargar Factura' para completarlos.">
                          <AppIcon name="warning" /> Parcialmente facturado
                        </b-badge>
                        <b-badge variant="info" class="ml-1"
                                 v-if="data.item.details && data.item.details.length && hasRemitPrices(data.item) && !isRemitLinked(data.item) && !isRemitPartiallyLinked(data.item)"
                                 v-b-tooltip.hover title="El remito tiene precios cargados pero aún no tiene factura. Usá 'Cargar Factura' para generarla.">
                          <AppIcon name="clock" /> Pendiente de facturar
                        </b-badge>
                        <b-badge variant="danger" class="ml-1"
                                 v-if="data.item.details && data.item.details.length && !hasRemitPrices(data.item)"
                                 v-b-tooltip.hover title="Hay ítems sin precio de costo. Editá el remito para completarlos antes de facturar.">
                          <AppIcon name="warning" /> Sin precios
                        </b-badge>
                      </div>
                    </template>
                    
                    <template v-slot:cell(date)="data">
                      {{moment(data.item.date).format('DD/MM/YYYY')}}
                    </template>
                    
                    <template v-slot:cell(suppliers_id)="data">  
                      <div v-if="data.item.supplier">            
                        <b-avatar :src="data.item.supplier.image"
                                  v-if="data.item.supplier.image"
                                  size="25px">
                        </b-avatar>
                        <b-avatar v-else 
                                  :text="avatarFromName(data.item.supplier.name).text"
                                  :style="avatarFromName(data.item.supplier.name).style"
                                  size="25px">
                        </b-avatar>                                                     
                        &nbsp;<b>{{data.item.supplier.name}}</b>   
                        
                        <AppIcon name="circle-fill" v-if="!data.item.supplier.active" class="ml-2 text-danger" v-b-tooltip.hover title="Proveedor INACTIVO" />
                      </div>
                    </template>

                    <template v-slot:cell(observations)="data">
                      <span v-if="data.item.file">
                        <b-link target="blank" :href="data.item.file" v-b-tooltip.hover title="Ver documento adjunto" class="text-center">
                          <AppIcon name="paperclip" />
                        </b-link>
                      </span>                                  
                      <span v-if="data.item.observations" v-b-tooltip.hover :title="data.item.observations" class="mr-1 text-center">
                        <span v-if="isMobileViewport">{{data.item.observations}}</span>
                        <AppIcon v-else name="chat-square-text" />
                      </span>                                                           
                    </template>
                    
                    <template v-slot:cell(f_action)="data">
                      <b-dropdown right
                                  no-caret
                                  size="sm"
                                  variant="link"
                                  boundary="window"
                                  :toggle-class="isMobileViewport ? 'purchase-remits-action-toggle-labeled' : 'app-table-action-toggle'"
                                  :class="{ 'pull-right': !isMobileViewport }">                        
                        <template #button-content>
                          <AppIcon name="more_vert" :label="isMobileViewport ? 'Opciones' : 'Acciones'" :class="{ 'mr-1': isMobileViewport }" />
                          <span v-if="isMobileViewport">Opciones</span>
                        </template>
                        <b-dropdown-item @click="sendPrinter(data.item)">
                          <AppIcon name="print" /> Imprimir
                        </b-dropdown-item>                                        
                        <b-dropdown-item @click="generateInvoice(data.item)" v-if="!isRemitLinked(data.item)">                          
                          <AppIcon name="receipt" /> Cargar Factura
                        </b-dropdown-item>                                  
                        <b-dropdown-header v-if="canShowActions(data.item)">Acciones</b-dropdown-header>
                        <b-dropdown-item @click="edit(data.item)" v-if="!hasRemitLinkedItems(data.item)">
                          <AppIcon name="edit" /> Editar
                        </b-dropdown-item>                          
                        <b-dropdown-item @click="remove(data.item)" v-if="access.elements.deletePurchaseRemits && !hasRemitLinkedItems(data.item)">
                          <AppIcon name="delete" class="text-danger" /> Eliminar
                        </b-dropdown-item>
                      </b-dropdown>
                    </template>
                </b-table>
                <EmptyState v-else icon="receipt" title="No se encontraron remitos de compra" description="Ajustá los filtros o el rango de fechas para ampliar la búsqueda, o cargá un remito nuevo con el botón Agregar." />

                <div class="app-table-footer purchase-remits-table-footer">
                  <span class="list-view-toolbar__count" v-if="table.tablePaginate">{{ table.tablePaginate.total }} registros</span>
                  <export-excel
                    class="list-view-link"
                    :data="arr.export"
                    worksheet="Listado de Remitos de Compras"
                    name="list-purchase-remits.xls">
                    <AppIcon name="download" class="mr-1" /> Exportar
                  </export-excel>
                </div>
              </b-col>
            </b-row>
          </b-card>
        </b-col>
      </b-row> 

      <b-sidebar
          id="sidebar-purchase-remit-detail"
          v-model="detailDrawer"
          right
          shadow
          backdrop
          title="Detalle de Remito de Compra"
          width="420px"
          @hidden="hideSub">
        <div class="list-view-drawer" v-if="itemSelected">
          <div class="list-view-drawer__header">
            <div class="list-view-drawer__title">
              <span class="list-view-drawer__code" v-if="itemSelected.type_voucher && itemSelected.point_sale">
                {{itemSelected.type_voucher.name}} {{itemSelected.point_sale.toString().padStart(4,'0') + '-' + itemSelected.number.toString().padStart(8,'0')}}
              </span>
              <div v-if="itemSelected.supplier">{{itemSelected.supplier.name}}</div>
              <div class="list-view-drawer__meta" v-if="itemSelected.date">
                {{moment(itemSelected.date).format('DD/MM/YYYY')}}
              </div>
            </div>
            <div class="list-view-drawer__nav">
              <b-button size="sm"
                        variant="outline-secondary"
                        :disabled="!canGoPrevRemit"
                        v-b-tooltip.hover
                        title="Remito anterior"
                        @click="goPrevRemit()">
                <AppIcon name="chevron-up" />
              </b-button>
              <b-button size="sm"
                        variant="outline-secondary"
                        :disabled="!canGoNextRemit"
                        v-b-tooltip.hover
                        title="Remito siguiente"
                        @click="goNextRemit()">
                <AppIcon name="chevron-down" />
              </b-button>
            </div>
          </div>

          <div class="list-view-drawer__stats">
            <div class="list-view-drawer__stat">
              <span class="list-view-drawer__stat-label">Items</span>
              <span class="list-view-drawer__stat-value">{{tableSub.items.length}}</span>
            </div>
            <div class="list-view-drawer__stat" :class="{ 'list-view-drawer__stat--warning': !hasRemitPrices(itemSelected) || isRemitPartiallyLinked(itemSelected) }">
              <span class="list-view-drawer__stat-label">Estado</span>
              <span class="list-view-drawer__stat-value">{{getRemitBillingStatusLabel(itemSelected)}}</span>
            </div>
          </div>

          <div>
            <div class="list-view-drawer__section-title">
              <AppIcon name="list-ul" /> Detalle
            </div>

            <b-table class="app-table app-table--compact"
                    responsive
                    stacked
                    :hover="true"
                    :small="true"
                    :fixed="false"
                    :items="tableSub.items"
                    :fields="tableSub.fields"
                    v-if="tableSub.items.length">

              <template v-slot:cell(quantity)="row"> 
                {{row.item.type_balance}} {{row.item.quantity}}
                <span v-if="row.item.unit_measurement">{{row.item.unit_measurement.reference}}</span>
              </template>

              <template v-slot:cell(product)="row">
                <div class="text-truncate" v-b-tooltip.hover :title="getDetailName(row.item)" v-html="getDetailName(row.item)"></div>
              </template>
            </b-table>
            <EmptyState v-else icon="list-ul" title="Sin items cargados" />
          </div>

          <b-alert v-for="difference in getRemitPurchaseDifferences(itemSelected)"
                   :key="'remit-purchase-difference-' + difference.purchase.id"
                   show
                   variant="warning"
                   class="mb-3">
            <AppIcon name="warning" class="mr-1" />
            La factura {{ getPurchaseReference(difference.purchase) }} ({{ formatRemitPurchaseAmount(difference.invoiceAmount) }}) difiere de los ítems de este remito vinculados a ella ({{ formatRemitPurchaseAmount(difference.remitAmount) }}).
          </b-alert>

          <b-button v-if="tableSub.items.length"
                    block
                    size="sm"
                    variant="outline-secondary"
                    class="text-left"
                    @click="openDetail()">
            <AppIcon name="visibility" class="mr-1" /> Ver detalle completo
          </b-button>

          <div class="list-view-drawer__actions">
            <b-button size="sm" variant="outline-secondary" @click="sendPrinter(itemSelected)">
              <AppIcon name="print" class="mr-1" /> Imprimir
            </b-button>
            <b-button size="sm" variant="outline-secondary" @click="generateInvoice(itemSelected)" v-if="!isRemitLinked(itemSelected)">
              <AppIcon name="receipt" class="mr-1" /> Cargar Factura
            </b-button>
            <b-button size="sm" variant="outline-secondary" @click="edit(itemSelected)" v-if="!hasRemitLinkedItems(itemSelected)">
              <AppIcon name="edit" class="mr-1" /> Editar
            </b-button>
          </div>
        </div>

        <template #footer>
          <div class="list-view-drawer__footer" v-if="itemSelected && access.elements.deletePurchaseRemits && !hasRemitLinkedItems(itemSelected)">
            <b-button size="sm" variant="outline-danger" block @click="remove(itemSelected)">
              <AppIcon name="delete" class="mr-1" /> Eliminar
            </b-button>
          </div>
        </template>
      </b-sidebar>

      <b-row>
        <b-col lg="12" id="view_footer_purchase_remits">  
          <b-card body-class="py-2">
            <div class="list-view-footer">
              <b-button type="button" variant="outline-dark" size="sm" @click="$router.go(-1)">
                <AppIcon name="back" />
                Volver
              </b-button>

              <nav v-if="table.tablePaginate">
                <b-pagination class="mb-0"
                              size="sm"
                              pills=""
                              :total-rows="table.tablePaginate.total"
                              :per-page="table.tablePaginate.per_page"
                              v-model="table.currentPage"
                              @input="filterPurchaseRemits()" />
              </nav>
            </div>
          </b-card>
        </b-col>
      </b-row>

      <!--
      ###########################
      ######### PRINTER #########
      ###########################
      -->
      <Printer v-if="crud.print.id" 
              :reference="crud.print.reference" 
              :registerID="crud.print.id"
              :key="crud.print.key"/>

      <!-- ########################### -->
      <!-- #####     MODALES     ##### -->
      <!-- ########################### -->

      <!-- CRUD MAIN -->
      <b-modal v-model="modal.form.active"
              no-close-on-esc
              no-close-on-backdrop
              hide-header-close
              size="xl"
              dialog-class="purchase-remits-form-modal-dialog"
              :key="modal.form._id"
              :id="modal.form._id">
        <div slot="modal-header" minimize class="app-modal-header">
          <span class="app-modal-header__icon"><AppIcon name="edit" /></span>
          <h5 class="app-modal-header__title">{{this.modal.form.title}}</h5>
        </div>

        <form-wizard  title=""
                      subtitle=""
                      nextButtonText="Siguiente"
                      backButtonText="Atrás"
                      finishButtonText="Guardar"
                      color="#2f353a"
                      shape="tab"
                      @on-complete="save"
                      class="crud-sales-wizard">

          <tab-content title="General" icon="fa fa-edit" :before-change="purchaseRemitsValidDetailGeneral">          
            <div class="purchase-remits-form-section">
              <div class="purchase-remits-form-section__title">
                <AppIcon name="description" /> Datos generales
              </div>
            <b-row v-if="crud.form.source_mode == 'from_order'" class="mb-2">
              <b-col md="12">
                <div class="app-alert app-alert--info">
                  <span class="app-alert__icon"><AppIcon name="info" /></span>
                  <div class="app-alert__content">Modo de carga: remito desde orden de compra</div>
                </div>
              </b-col>
            </b-row>
            <b-row>      
              <b-col md="4">
                <b-form-group label="Fecha">
                  <b-form-datepicker size="sm" v-model="crud.form.date" placeholder="Fecha" local="es"></b-form-datepicker>                      
                </b-form-group>            
              </b-col>           
            </b-row>    
            <b-row> 
              <b-col md="5">
                <FindObject render="search"
                            type="suppliers" 
                            @select-object="loadSuppliers($event)" 
                            :valueID="crud.form.suppliers_id"
                            :where="conditionSuppliersActive"
                            :disabled="crud.form.id>0" />                 
              </b-col>                                                                                             
              <b-col md="3"> 
                <FindObject tag="Punto de Venta (Imputación)"
                            render="search"
                            type="erpPointsSales" 
                            display="full_identification"
                            @select-object="loadPointsSales($event)" 
                            :valueID="crud.form.points_sales_id"
                            :key="'finder_point_sale_'+componentKey"
                            auto-select-single
                            :disabled="crud.form.id>0" 
                            :where="conditionPointsSalesActive" />                 
              </b-col>                                         
            </b-row>
            <b-row> 
              <b-col md="3">
                <b-form-group label="Comprobante">
                  <div class="purchase-remits-voucher-type">
                    <span class="purchase-remits-voucher-type__icon"><AppIcon name="receipt" /></span>
                    <span>
                      <strong>Remito</strong>
                      <small>Ingreso de mercadería</small>
                    </span>
                  </div>
                </b-form-group>
              </b-col>                
              <b-col md="2">
                <b-form-group label="Punto de Venta">    
                  <app-text-input v-model.number="crud.form.point_sale"
                                  type="text"
                                  inputmode="numeric"
                                  :allowed-pattern="/[0-9]/g"
                                  size="sm"
                                  maxlength="6" />
                </b-form-group>                            
              </b-col>  
              <b-col md="3">
                <b-form-group label="Número">    
                  <app-text-input v-model.number="crud.form.number"
                                  type="text"
                                  inputmode="numeric"
                                  :allowed-pattern="/[0-9]/g"
                                  size="sm"
                                  maxlength="10" />
                </b-form-group>                            
              </b-col>                         
            </b-row>             
            </div>
          </tab-content>

          <tab-content title="Orden de Compra"
                      icon="fa fa-file-text-o"
                      v-if="crud.form.source_mode == 'from_order'"
                      :before-change="purchaseRemitsValidDetailPurchaseOrder">
            <div class="purchase-remits-form-section">
              <div class="purchase-remits-form-section__title">
                <AppIcon name="cart" /> Orden de Compra
              </div>
            <b-row>
              <b-col md="12">
                <b-form-group label="Ordenes de compra pendientes del proveedor">
                  <v-select :options="arr.purchaseOrdersPending"
                            v-model="crud.form.purchase_order"
                            placeholder="Seleccionar orden de compra"
                            :multiple="false"
                            :select-on-tab="true"
                            @input="loadPurchaseOrderToRemit($event)" />
                </b-form-group>
              </b-col>
            </b-row>
            <b-row>
              <b-col md="12">
                <div class="app-alert app-alert--success" v-if="crud.form.purchase_orders_id">
                  <span class="app-alert__icon"><AppIcon name="check2-circle" /></span>
                  <div class="app-alert__content">OC seleccionada: <b>{{getPurchaseOrderLabel(crud.form.purchase_order)}}</b></div>
                </div>
                <div class="app-alert app-alert--warning" v-else>
                  <span class="app-alert__icon"><AppIcon name="exclamation-triangle" /></span>
                  <div class="app-alert__content">Debe seleccionar una orden de compra pendiente para precargar el detalle del remito.</div>
                </div>
              </b-col>
            </b-row>
            <b-row v-if="crud.form.purchase_orders_id">
              <b-col md="12">
                <b-table class="app-table app-table--compact table-purchase-remits-from-order"
                         responsive="sm"
                         :stacked="isMobileViewport ? 'md' : false"
                         :small="true"
                         :fixed="false"
                         :items="purchaseOrderGridItems"
                         :fields="purchaseOrderGridFields">

                  <template v-slot:cell(item)="data">
                    <div class="purchase-remits-detail-item">
                      <b>{{getPurchaseOrderGridItemCode(data.item.detail)}}</b>
                      <span class="ml-1">{{getPurchaseOrderGridItemName(data.item.detail)}}</span>
                    </div>
                  </template>

                  <template v-slot:cell(quantity)="data">
                    <b-input-group size="sm">
                      <b-form-input v-model.number="data.item.detail.quantity"
                                    type="number"
                                    min="0"
                                    step="0.0001"
                                    @change="normalizePurchaseOrderDetailQuantity(data.item.detail)" />
                      <template #append>
                        <b-input-group-text>{{getPurchaseOrderGridItemUnit(data.item.detail)}}</b-input-group-text>
                      </template>
                    </b-input-group>
                  </template>

                  <template v-slot:cell(location)="data">
                    <b-form-select size="sm"
                                   v-model="data.item.detail.location_id"
                                   :options="arr.purchaseOrderLocationOptions"
                                   @change="setPurchaseOrderItemLocation(data.item.detail, $event)" />
                  </template>

                </b-table>
              </b-col>
            </b-row>
            <b-row v-if="crud.form.purchase_orders_id && !purchaseOrderGridItems.length">
              <b-col md="12">
                <div class="app-alert app-alert--warning">
                  <span class="app-alert__icon"><AppIcon name="exclamation-triangle" /></span>
                  <div class="app-alert__content">La orden seleccionada no posee ítems pendientes para remitir.</div>
                </div>
              </b-col>
            </b-row>
            </div>
          </tab-content>

          <tab-content title="Detalle" icon="fa fa-list" :before-change="purchaseRemitsValidDetailItems">          
            <div class="purchase-remits-form-section">
              <div class="purchase-remits-form-section__title">
                <AppIcon name="list-ul" /> Productos cargados
              </div>
            <b-row>
              <b-col lg="12" md="12" sm="12">                                  
                <Detail @load-detail-products="setDetailItems($event, 'products')"
                        @load-detail-products-compound="setDetailItems($event, 'productsCompound')"
                        :operation="crud.form"
                        :detailProducts="detailProductsForDetailTab"
                        :detailProductsCompound="detailProductsCompoundForDetailTab"
                        :allowPurchasePricing="true"
                        :currency="getCurrency"
                        :allowMultipleProducts="true"
                        :allowInlineDetailEdit="true"
                        :key="'detail_' + componentKey"
                        :forceUpdate="componentKey" />                                             
              </b-col>        
            </b-row>
            </div>
          </tab-content>

          <tab-content title="Observaciones" icon="fa fa-flag-checkered" :key="'tab_f_'+componentKey">          
            <div class="purchase-remits-form-section">
              <div class="purchase-remits-form-section__title">
                <AppIcon name="chat-square-text" /> Observaciones
              </div>
            <b-row>
              <b-col md="12">
                <b-form-group label="Observaciones">
                  <app-textarea
                    v-model="crud.form.observations"
                    placeholder="Ingresar las observaciones..."
                    rows="3"
                    max-rows="6"
                  ></app-textarea>
                </b-form-group>
              </b-col>
            </b-row>
            <b-row v-if="!crud.form.id">
              <b-col md="12">
                <div class="app-alert purchase-remits-invoice-toggle" :class="loadInvoiceAfterSave ? 'app-alert--success' : 'app-alert--info'">
                  <span class="app-alert__icon"><AppIcon name="receipt" /></span>
                  <div class="app-alert__content">
                    <b-form-checkbox v-model="loadInvoiceAfterSave" switch class="mb-0 font-weight-bold">
                      Cargar la factura de este remito ahora
                    </b-form-checkbox>
                    <div class="mt-1" v-if="loadInvoiceAfterSave">
                      Al generar el remito, se abre directamente la carga de la factura de compra con estos datos precargados.
                    </div>
                  </div>
                </div>
              </b-col>
            </b-row>
            </div>
          </tab-content>

        </form-wizard>
        
        <div slot="modal-footer">
          <b-button variant="outline-secondary" class="mr-1" @click="cancel()">
            <AppIcon name="x" class="mr-1" />Cancelar
          </b-button>
        </div>                
      </b-modal>

      <!-- CRUD DETAILS -->
      <b-modal v-model="modal.detail.active"
              size="xl"
              :key="modal.detail._id"
              :id="modal.detail._id">                  
        <div slot="modal-header" minimize class="app-modal-header">
          <span class="app-modal-header__icon"><AppIcon name="visibility" /></span>
          <h5 class="app-modal-header__title">{{this.modal.detail.title}}</h5>
        </div>

        <b-row>
          <b-col sm="12">                     
            <b-tabs active-nav-item-class="font-weight-bold text-uppercase text-dark">
              <b-tab>
                <template #title><AppIcon name="description" class="mr-1" /> General</template>
                <div class="app-modal-detail-list" v-if="itemSelected">
                  <b-alert v-for="difference in getRemitPurchaseDifferences(itemSelected)"
                           :key="'remit-detail-purchase-difference-' + difference.purchase.id"
                           show
                           variant="warning"
                           class="mb-3">
                    <AppIcon name="warning" class="mr-1" />
                    La factura {{ getPurchaseReference(difference.purchase) }} ({{ formatRemitPurchaseAmount(difference.invoiceAmount) }}) difiere de los ítems de este remito vinculados a ella ({{ formatRemitPurchaseAmount(difference.remitAmount) }}).
                  </b-alert>
                  <div class="app-modal-detail-list__row">
                    <span class="app-modal-detail-list__label">ID</span>
                    <span class="app-modal-detail-list__value"><b>#{{this.itemSelected.id}}</b></span>
                  </div>
                  <div class="app-modal-detail-list__row" v-if="itemSelected.points_sales && itemSelected.points_sales.business">
                    <span class="app-modal-detail-list__label">Empresa</span>
                    <span class="app-modal-detail-list__value">{{this.itemSelected.points_sales.business.reference}}</span>
                  </div>
                  <div class="app-modal-detail-list__row" v-if="itemSelected.points_sales">
                    <span class="app-modal-detail-list__label">Punto de Venta Imputación</span>
                    <span class="app-modal-detail-list__value">{{this.itemSelected.points_sales.point_sale}} - {{this.itemSelected.points_sales.name}}</span>
                  </div>
                  <div class="app-modal-detail-list__row" v-if="itemSelected.type_voucher && itemSelected.point_sale">
                    <span class="app-modal-detail-list__label">Comprobante</span>
                    <span class="app-modal-detail-list__value"><b>{{this.itemSelected.type_voucher.name + ' '  + this.itemSelected.point_sale.toString().padStart(4,'0') + '-' + this.itemSelected.number.toString().padStart(8,'0')}}</b></span>
                  </div>
                  <div class="app-modal-detail-list__row" v-if="itemSelected.staff">
                    <span class="app-modal-detail-list__label">Emisor</span>
                    <span class="app-modal-detail-list__value">{{itemSelected.staff.name}}</span>
                  </div>
                  <div class="app-modal-detail-list__row">
                    <span class="app-modal-detail-list__label">Fecha</span>
                    <span class="app-modal-detail-list__value">{{moment(this.itemSelected.date).format('DD MMMM YYYY')}}</span>
                  </div>
                  <div class="app-modal-detail-list__row" v-if="itemSelected.supplier">
                    <span class="app-modal-detail-list__label">Proveedor</span>
                    <span class="app-modal-detail-list__value">{{this.itemSelected.supplier.name}}</span>
                  </div>
                  <div class="app-modal-detail-list__row">
                    <span class="app-modal-detail-list__label">Estado factura</span>
                    <span class="app-modal-detail-list__value">
                      <b-badge variant="success"
                               v-if="isRemitLinked(itemSelected)"
                               v-b-tooltip.hover
                               title="Todos los ítems tienen factura vinculada">
                        <AppIcon name="check2-circle" /> Factura vinculada
                      </b-badge>
                      <b-badge variant="warning"
                               v-else-if="isRemitPartiallyLinked(itemSelected)"
                               v-b-tooltip.hover
                               title="Algunos ítems aún no tienen factura. Usá 'Cargar Factura' para completarlos.">
                        <AppIcon name="warning" /> Parcialmente facturado
                      </b-badge>
                      <b-badge variant="info"
                               v-else-if="itemSelected.details && itemSelected.details.length && hasRemitPrices(itemSelected)"
                               v-b-tooltip.hover
                               title="El remito tiene precios cargados pero aún no tiene factura. Usá 'Cargar Factura' para generarla.">
                        <AppIcon name="clock" /> Pendiente de facturar
                      </b-badge>
                      <b-badge variant="danger"
                               v-else-if="itemSelected.details && itemSelected.details.length && !hasRemitPrices(itemSelected)"
                               v-b-tooltip.hover
                               title="Hay ítems sin precio de costo. Editá el remito para completarlos antes de facturar.">
                        <AppIcon name="warning" /> Sin precios
                      </b-badge>
                      <span v-else>Sin ítems</span>
                    </span>
                  </div>
                  <div class="app-modal-detail-list__row" v-if="itemSelected.observations">
                    <span class="app-modal-detail-list__label">Observaciones</span>
                    <span class="app-modal-detail-list__value">{{this.itemSelected.observations}}</span>
                  </div>
                </div>
              </b-tab>    

              <b-tab>
                <template #title><AppIcon name="list-ul" class="mr-1" /> Items</template>
                <b-table class="mb-0 app-table app-table--compact table-full-detail-purchase-remits"
                        responsive
                        :stacked="isMobileViewport ? 'md' : false"
                        :hover="true"
                        :small="true"
                        :fixed="true"
                        :items="tableDetail.items"
                        :fields="tableDetail.fields"
                        v-if="tableDetail.items.length">                       
                    
                  <template v-slot:table-colgroup="scope">
                    <col
                      v-for="field in scope.fields"                    
                      :key="field.key"
                      :style="{ width: field.width }"
                    >
                  </template>           

                  <template v-slot:cell(product)="data">                           
                    <div class="purchase-remits-detail-item">
                      <div class="purchase-remits-detail-item__code" v-html="getDetailCode(data.item)"></div>
                      <div class="purchase-remits-detail-item__name text-truncate" v-b-tooltip.hover :title="getDetailName(data.item)" v-html="getDetailName(data.item)"></div>
                    </div>
                  </template>

                  <template v-slot:cell(deposit)="data">   
                    <div class="purchase-remits-detail-location" v-if="data.item.location">
                      <DepositLocationLabel :location="data.item.location" />
                    </div>
                  </template>

                  <template v-slot:cell(purchase)="data">
                    <span v-if="data.item.purchase">
                      {{getPurchaseReference(data.item.purchase)}}
                    </span>
                    <span v-else class="text-muted">Sin factura</span>
                  </template>

                  <template v-slot:cell(quantity)="data">   
                    {{data.item.quantity}}
                    <span v-if="data.item.unit_measurement">{{data.item.unit_measurement.reference}}</span>
                  </template>

                  <template v-slot:cell(price_cost)="data">   
                    {{Intl.NumberFormat('es-AR',{style:'currency',currency:getCurrency}).format(data.item.price_cost)}}                                    
                  </template>

                  <template v-slot:cell(price_total)="data">   
                    {{Intl.NumberFormat('es-AR',{style:'currency',currency:getCurrency}).format(parseFloat(data.item.quantity) * parseFloat(data.item.price_cost))}}                                    
                  </template>
                </b-table>
                <EmptyState v-else icon="list-ul" title="No se encontraron items" />
              </b-tab>              
            </b-tabs>
          </b-col>
        </b-row>

        <div slot="modal-footer">
          <b-button variant="outline-secondary" class="mr-1" @click="modal.detail.active=false">
            <AppIcon name="x" class="mr-1" />Cerrar
          </b-button>
        </div>
      </b-modal>


    </b-col>
  </b-row>
</template>
<script>
  import serviceAPI from './services'
  import Error from '@/handler/error'
  import ErrorToken from '@/handler/errorToken'
  import Profiles from '@/config/profiles'
  import Modules from '@/config/modules'
  import Helper from '@/handler/helper' 
  import Storage from '@/handler/storageSession' 
  import FindObject from '@/components/inc/find/findObject'  
  import moment from 'moment'
  import Printer from '@/components/inc/printer/printer'  
  import Detail from '@/components/inc/cpbtDetailDeposit/detail.vue'
  import {FormWizard, TabContent} from 'vue-form-wizard'
  import 'vue-form-wizard/dist/vue-form-wizard.min.css'
  import Function from '@/handler/funciones'
  import StorageLocal from '@/handler/storageLocal'
  import TabHeaderActions from '@/components/inc/tabs/TabHeaderActions'

  export default { 
    components: {
      FindObject,
      Detail,
      FormWizard,
      TabContent,      
      Printer,
      TabHeaderActions,
    },       
    data: () => {
      return {
        access: {
          module_id: Modules.REMITOS_COMPRAS,
          profile_id: Profiles.PERSONAL,
          view_reference: 'purchaseRemits',
          elements: {
            deletePurchaseRemits: true
          }
        },
        table : {
          items: [],
          fields: [],
          filter: null,
          mostrarFiltros: true,
          currentPage: 1,
          perPage: 0,
          isBusy: false,
          tablePaginate: null,          
        },   
        tableSub : {
          items: [],
          fields: []
        },    
        tableDetail : {
          items: [],
          fields: []
        },             
        purchaseOrderGridFields: [
          {key: 'item', label: 'Producto', class: "align-middle"},
          {key: 'quantity', label: 'Cantidad', class: "align-middle text-center", thStyle: {width: '130px'}},
          {key: 'location', label: 'Ubicación', class: "align-middle", thStyle: {width: '200px'}},
        ],
        crud: {
          form: {
            id: 0,
            date: '',
            types_vouchers_id: 0,
            types_vouchers: null,
            letter: '',
            point_sale: 0,
            number: 0,
            observations: '',            
            staff_id: 0,              
            suppliers_id: 0,
            suppliers: null,      
            points_sales_id: 0,
            points_sales: null, 
            selectTypeVoucher: 'remito',
            reference_voucher_id: 0,                                      
            source_mode: 'normal',
            purchase_orders_id: 0,
            purchase_order: null,
            detailProducts: [],
            detailProductsCompound: [],
            type: {
              type_balance: '+',
            }
          },
          print: {
            reference: '',
            id: 0,
            key: 0,
          }                        
        },          
        modal: {
          form: {
            active: false,
            title: '',
          },
          detail: {
            active: false,
            title: ''
          },
        },                     
        arr: {
          purchaseRemits: [],   
          purchaseOrdersPending: [],
          purchaseOrderLocations: [],
          purchaseOrderLocationOptions: [],
          export: [],          
          filters : {
            suppliers: [],
          }
        },           
        filters: {
          date_start: '',
          date_end: '',
          suppliers: null,
        },  
        itemSelected: null,
        componentKey: 0,
        detailDrawer: false,
        filtersOpen: false,
        isMobileViewport: false,
        loadInvoiceAfterSave: false,
        purchaseOrderZeroCache: {
          products: [],
          productsCompound: [],
        },
      }
    },
    created () {
      /* Configurar permisos de vistas y elementos */
      Helper.hasAccessView(this.access)
      this.access.elements.deletePurchaseRemits = Helper.hasAccessElement(this.access, 'delete_purchase_remits') 
      /* Fin configuracion */
    },
    computed: {
      // CONDITIONS SELECT
      conditionSuppliersActive(){
        return [{field: 'active', condition: true}, {field: 'shipping', condition: false}];
      },
      conditionPointsSalesActive() {
        return [{field: 'active', condition: true}];
      },        

      getCurrency(){
        return "ARS"
      },

      purchaseOrderGridItems() {
        var items = []
        this.crud.form.detailProducts.forEach((element) => {
          items.push({ type: 'product', detail: element })
        })
        this.crud.form.detailProductsCompound.forEach((element) => {
          items.push({ type: 'compound', detail: element })
        })
        return items
      },
      detailProductsForDetailTab() {
        if(this.crud.form.source_mode != 'from_order') {
          return this.crud.form.detailProducts || []
        }
        return (this.crud.form.detailProducts || []).filter((item) => parseFloat(item.quantity || 0) > 0)
      },
      detailProductsCompoundForDetailTab() {
        if(this.crud.form.source_mode != 'from_order') {
          return this.crud.form.detailProductsCompound || []
        }
        return (this.crud.form.detailProductsCompound || []).filter((item) => parseFloat(item.quantity || 0) > 0)
      },
      currentRemitIndex() {
        if(!this.itemSelected) return -1
        return this.table.items.findIndex(item => item.id === this.itemSelected.id)
      },
      canGoPrevRemit() {
        return this.currentRemitIndex > 0
      },
      canGoNextRemit() {
        return this.currentRemitIndex >= 0 && this.currentRemitIndex < this.table.items.length - 1
      },
    },
    mounted() {
      this.filtersOpen = StorageLocal.getValue('filters_open_crud_purchase_remits') == 'true'

      this.mobileMql = window.matchMedia('(max-width: 767.98px)')
      this.isMobileViewport = this.mobileMql.matches
      this.mobileMql.addEventListener('change', this.updateMobileViewport)

      this.loadFieldTable()
      this.filterLoad()
      this.filterPurchaseRemits()

      // Acción directa desde la paleta de comandos: abrir el alta al entrar
      // (query.mode: 'from_order' abre el alta desde orden de compra, ver
      // addFromPurchaseOrder(); cualquier otro valor u omitido, el alta
      // normal).
      if (this.$route.query.new == '1') {
        if (this.$route.query.mode == 'from_order') {
          this.addFromPurchaseOrder()
        } else {
          this.add()
        }
        // Limpia el query apenas se consume -- si no, la pestaña queda con
        // ?new=1 pegado para siempre y volver a ella (activated, keep-alive)
        // reabriría el alta de nuevo aunque el usuario solo quiera ver el
        // listado.
        this.$router.replace({ path: this.$route.path, query: {} }).catch(() => {})
      }

      this._start = true
    },
    beforeDestroy() {
      if(this.mobileMql) {
        this.mobileMql.removeEventListener('change', this.updateMobileViewport)
      }
    },
    activated() {
      if(this._start) {
        this._start = false
        return false
      }

      this.filterLoad()
      this.filterPurchaseRemits()

      // Misma acción directa que en mounted(): si la pestaña ya estaba
      // montada (keep-alive), "mounted" no vuelve a correr y la paleta de
      // comandos no abriría nada sin este chequeo acá también.
      if (this.$route.query.new == '1') {
        if (this.$route.query.mode == 'from_order') {
          this.addFromPurchaseOrder()
        } else {
          this.add()
        }
        // Limpia el query apenas se consume -- si no, la pestaña queda con
        // ?new=1 pegado para siempre y volver a ella (activated, keep-alive)
        // reabriría el alta de nuevo aunque el usuario solo quiera ver el
        // listado.
        this.$router.replace({ path: this.$route.path, query: {} }).catch(() => {})
      }
    },
    watch: {        
      'crud.form.suppliers_id': function () {                         
        this.searchVoucherBySupplier()
      },
      'crud.form.points_sales_id': function () {                        
        this.searchVoucherBySupplier()
      },
      filtersOpen(value) {
        StorageLocal.setValue('filters_open_crud_purchase_remits', value ? 'true' : 'false')
      },
      isMobileViewport() {
        this.loadFieldTable()
      },
    },        
    methods: {
      // CONFIGURACION
      updateMobileViewport(e) {
        this.isMobileViewport = e.matches
      },
      alignClass(desktopClass) {
        return this.isMobileViewport ? 'text-left' : desktopClass
      },
      avatarFromName(name) {
        return Function.avatarFromName(name)
      },
      confirmDialog(message, options) {
        var danger = !!options.danger
        var icon = options.icon || (danger ? 'warning' : 'help')
        var title = this.$createElement('span', { class: 'app-confirm-header__title' }, [
          this.$createElement('AppIcon', { props: { name: icon } }),
          options.title
        ])
        return this.$bvModal.msgBoxConfirm(message, {
          title: title,
          size: options.size || 'sm',
          buttonSize: options.buttonSize || 'sm',
          okVariant: danger ? 'danger' : 'dark',
          okTitle: options.okTitle,
          cancelTitle: options.cancelTitle || 'Cancelar',
          footerClass: 'p-2',
          headerClass: danger ? 'app-confirm-header app-confirm-header--danger' : 'app-confirm-header',
          hideHeaderClose: false,
          centered: true
        })
      },
      loadFieldTable () {
        this.table.fields = []
        this.tableSub.fields = []
        this.tableDetail.fields = []

        // TABLE COMPROBANTES
        this.table.fields.push({key: 'business', label: 'Empresa', class:"align-middle text-left", width:"60px"})
        this.table.fields.push({key: 'voucher', label: 'Comprobante', class:"align-middle text-left", width:"150px"})
        this.table.fields.push({key: 'date', label: 'Fecha', class:"align-middle " + this.alignClass('text-center'), width:"90px"})
        this.table.fields.push({key: 'suppliers_id', label: 'Proveedor', class:"align-middle", width:"150px"})
        this.table.fields.push({key: 'observations', label: '', class:"align-middle " + this.alignClass('text-center'), width:"25px"})
        this.table.fields.push({key: 'f_action', label: this.isMobileViewport ? 'Acción' : '', class:"align-middle " + this.alignClass('text-right'), width:"70px"})

        // TABLE DETALLE
        this.tableSub.fields.push({key: 'quantity', label: 'Cantidad', class:"align-middle", width:"50px"})                
        this.tableSub.fields.push({key: 'product', label: 'Item', class:"align-middle", width:"150px"})  
        
        // TABLE DETALLE COMPLETO        
        this.tableDetail.fields.push({key: 'product', label: 'Item', class:"align-middle text-left", width:"250px"})                                         
        this.tableDetail.fields.push({key: 'deposit', label: 'Ubicación', class:"align-middle text-left", width:"100px"})          
        this.tableDetail.fields.push({key: 'purchase', label: 'Factura', class:"align-middle", width:"120px"})          
        this.tableDetail.fields.push({key: 'quantity', label: 'Cant.', class:"align-middle " + this.alignClass('text-right'), width:"60px"})            
        this.tableDetail.fields.push({key: 'price_cost', label: 'Precio', class:"align-middle " + this.alignClass('text-right'), width:"70px"})     
        this.tableDetail.fields.push({key: 'price_total', label: 'Total', class:"align-middle " + this.alignClass('text-right'), width:"80px"})     
      }, 
      onRowSelected(item) {         
        this.arr.purchaseRemits.forEach((element, index) => {
          this.table.items[index]._showDetails = false
          if(item.length){
            if(element.id == item[0].id) {  
              this.openSub(element)                             
            }
          }
        })
      },       
      onRowClicked(item, index, event) {
        if(!item) return

        if(event && event.target) {
          const ignoreTags = ['INPUT', 'BUTTON', 'A', 'SELECT', 'TEXTAREA', 'LABEL', 'SVG', 'PATH']
          if(ignoreTags.includes(event.target.tagName)) return
          if(event.target.closest('.dropdown')) return
        }

        this.openSub(item)
      },
      rowClass(item, type) {
        if(type !== 'row' || !item) return ''
        if(this.detailDrawer && this.itemSelected && item.id === this.itemSelected.id) {
          return 'app-table-row-active'
        }
        return ''
      },
      goPrevRemit() {
        if(!this.canGoPrevRemit) return
        this.openSub(this.table.items[this.currentRemitIndex - 1])
      },
      goNextRemit() {
        if(!this.canGoNextRemit) return
        this.openSub(this.table.items[this.currentRemitIndex + 1])
      },
      forceRender() {
        this.componentKey += 1;  
      },

      // EXPORT
      prepareExport(data) {             
        this.arr.export = []
        data.forEach(element => {
          var businessID = 0
          var businessName = ""          
          var suppliersID = 0
          var suppliersCode = ""
          var suppliersName = ""
          var typeVoucherID = 0
          var typeVoucherName = ""
          var pointSalesID = 0
          var pointSalesName = ""

          if(element.points_sales && element.points_sales.business) {
            businessID = element.points_sales.business.id            
            businessName = element.points_sales.business.reference
          }
          if(element.supplier) {
            suppliersID = element.supplier.id
            suppliersCode = element.supplier.code
            suppliersName = element.supplier.name            
          }       

          if(element.type_voucher) {
            typeVoucherID = element.type_voucher.id
            typeVoucherName = element.type_voucher.name                        
          }

          if(element.points_sales) {
            pointSalesID = element.points_sales.id
            pointSalesName = element.points_sales.name                        
          }          

          this.arr.export.push({
            id: element.id,
            businessID: businessID,
            businessName: businessName,            
            date: element.date,          
            typeVoucherID: typeVoucherID,
            typeVoucherName: typeVoucherName,
            letter: element.letter,
            point_sale: element.point_sale,
            number: element.number,            
            suppliersID: suppliersID,
            suppliersCode: suppliersCode,
            suppliersName: suppliersName,            
            observations: element.observations,
            pointSalesID: pointSalesID,
            pointSalesName: pointSalesName,      
            productID: 0,
            productCode: "",
            productName: "",            
            productCompoundID: 0,
            productCompoundName: "",                                                      
            quantity: 0,
            unitMeasurementID: 0,
            unitMeasurementName: "",                      
          })  

          element.details.forEach(element1 => {
            var productID = 0
            var productCode = ""
            var productName = ""
            var productCompoundID = 0
            var productCompoundName = ""     
            var unitMeasurementID = 0
            var unitMeasurementName = ""               

            if(element1.products_id) {
              productID = element1.products_id
              productCode = element1.products_code
              productName = element1.products_name            
            }

            if(element1.products_colors_id) {
              productID = element1.products_colors_id
              productCode = element1.products_code
              productName = element1.products_name
            }            

            if(element1.products_waist_id) {
              productID = element1.products_waist_id
              productCode = element1.products_code
              productName = element1.products_name
            }            

            if(element1.products_color_waist_id) {
              productID = element1.products_color_waist_id
              productCode = element1.products_code
              productName = element1.products_name
            }       
          
            if(element1.products_compound_id) {
              productCompoundID = element1.products_compound_id
              productCompoundName = element1.products_name
            }    

            if(element1.unit_measurement_id) {
              unitMeasurementID = element1.unit_measurement.id
              unitMeasurementName = element1.unit_measurement.name            
            }            

            this.arr.export.push({
              productID: productID,
              productCode: productCode,
              productName: productName,
              productCompoundID: productCompoundID,
              productCompoundName: productCompoundName,                                                        
              quantity: element1.quantity.replace('.',','),
              unitMeasurementID: unitMeasurementID,
              unitMeasurementName: unitMeasurementName,              
            })
          })          
        });
      },

      // ABM MAIN
      add() {										
        this.crud.form.id = 0           
        this.crud.form.date = moment().format('YYYY-MM-DD')        
        this.crud.form.observations = ''
        this.crud.form.staff_id = Helper.getStaffId()        
        this.crud.form.suppliers_id = 0
        this.crud.form.suppliers = null                                   
        this.crud.form.types_vouchers_id = 0
        this.crud.form.types_vouchers = null                     
        this.crud.form.letter = ''
        this.crud.form.point_sale = 0
        this.crud.form.number = 0         
        this.crud.form.points_sales_id = 0
        this.crud.form.points_sales = null
        this.crud.form.source_mode = 'normal'
        this.crud.form.purchase_orders_id = 0
        this.crud.form.purchase_order = null
        
        this.crud.form.detailProducts = []
        this.crud.form.detailProductsCompound = []
        this.arr.purchaseOrdersPending = []
        this.loadInvoiceAfterSave = false

        this.modal.form._id = Math.random().toString(36).slice(2, 8)
        this.modal.form.active = true
        this.modal.form.title = 'Remito de Compra'
      },
      addFromPurchaseOrder() {
        this.add()
        this.crud.form.source_mode = 'from_order'
        this.modal.form.title = 'Remito de Compra desde Orden de Compra'
        this.loadAvailableLocations()
      },
      edit(item) {										
        this.crud.form.id = item.id
        this.crud.form.date = moment(item.date).format('YYYY-MM-DD')        
        this.crud.form.observations = item.observations
        this.crud.form.staff_id = item.staff_id
        this.crud.form.suppliers_id = item.suppliers_id
        this.crud.form.suppliers = item.supplier                   
        this.crud.form.types_vouchers_id = item.types_vouchers_id
        this.crud.form.types_vouchers = item.type_voucher
        this.crud.form.letter = item.letter
        this.crud.form.point_sale = item.point_sale
        this.crud.form.number = item.number
        this.crud.form.points_sales_id = item.points_sales_id
        this.crud.form.points_sales = item.points_sales
        this.crud.form.purchase_orders_id = item.purchase_orders_id ? parseInt(item.purchase_orders_id) : 0
        this.crud.form.purchase_order = null
        this.crud.form.source_mode = this.crud.form.purchase_orders_id ? 'from_order' : 'normal'
        this.arr.purchaseOrdersPending = []
        
        var result = serviceAPI.mostrarRemitos(item.id);

        result.then((response) => {  
          var data = response.data

          if(data) {
            if(this.crud.form.source_mode == 'from_order') {
              this.loadAvailableLocations()
            }

            if(data.details.products) {
              this.crud.form.detailProducts = data.details.products.map((element) => {
                return this.mapPurchaseOrderDetailItem(element, 'product')
              })
            }            

            if(data.details.products_compound) {
              this.crud.form.detailProductsCompound = data.details.products_compound.map((element) => {
                return this.mapPurchaseOrderDetailItem(element, 'compound')
              })
            }

            if(data.purchase_order) {
              this.crud.form.purchase_order = {
                code: data.purchase_order.id,
                label: this.getPurchaseOrderLabel(data.purchase_order),
                data: data.purchase_order,
              }
            }

            if(this.crud.form.source_mode == 'from_order' && this.crud.form.suppliers_id) {
              this.loadPendingPurchaseOrders(false)
            }

            this.forceRender()
          } 
        })    
                
        this.modal.form._id = Math.random().toString(36).slice(2, 8)
        this.modal.form.active = true
        this.modal.form.title = 'Editar Remito de Compra'
      },       
      remove(item) {
        this.crud.form.id = item.id
        
        var voucher = "ID #" + item.id
        if(item.type_voucher && item.point_sale) {
          voucher = item.type_voucher.name + ' ' + item.point_sale.toString().padStart(4,'0') + '-' + item.number.toString().padStart(8,'0')
        }        

        this.confirmDialog('¿Desea borrar el remito de compra ' + voucher + '?', {
          title: 'Borrar Remito',
          icon: 'delete',
          danger: true,
          okTitle: 'SI',
          cancelTitle: 'NO'
        })
        .then(value => {
          if (value) {
            let loader = this.$loading.show();
            var result = serviceAPI.eliminarRemitos(this.crud.form.id);

            result.then(() => {      
              this.modal.form.active = false        
              loader.hide()
              this.filterPurchaseRemits()
              this.$awn.success("Registro eliminado");
            })
            .catch(error => {
              loader.hide()
              this.$awn.alert(Error.showError(error));
            })
          }
        })
        .catch(error => {
          this.$awn.alert(Error.showError(error));
        })
      },          
      save() {
        if(this.crud.form.source_mode == 'from_order' && !this.crud.form.purchase_orders_id) {
          this.$awn.alert('Debe seleccionar una orden de compra pendiente')
          return false
        }
        if(this.crud.form.source_mode == 'from_order' && !this.purchaseOrderGridItems.length) {
          this.$awn.alert('La orden de compra seleccionada no posee ítems para remitir')
          return false
        }

        var payload = this.getRemitPayloadForSave()
        if(this.crud.form.source_mode == 'from_order' &&
           !payload.detailProducts.length &&
           !payload.detailProductsCompound.length) {
          this.$awn.alert('Debe cargar al menos un ítem con cantidad mayor a cero')
          return false
        }

        if(this.loadInvoiceAfterSave) {
          // Mismo chequeo que generateInvoice()/hasRemitPrices(): si falta algún precio de
          // costo, la factura quedaría en $0 — se valida acá, antes de guardar, con los mismos
          // datos del payload (evita un viaje extra al servidor para volver a pedirlos).
          var allDetailItems = (payload.detailProducts || []).concat(payload.detailProductsCompound || [])
          var hasAllPrices = !allDetailItems.length || allDetailItems.every((item) => parseFloat(item.price_cost) > 0)
          if(!hasAllPrices) {
            this.$awn.alert('Hay ítems sin precio de costo. Completá los precios o desmarcá "Cargar la factura de este remito ahora" para continuar.')
            return false
          }
        }

        this.confirmDialog('¿Desea continuar con la generación del remito de compra?', {
          title: 'Generar Remito',
          icon: 'save',
          okTitle: 'GENERAR',
          cancelTitle: 'Cancelar'
        })
        .then(value => {
          if (value) {
            let loader = this.$loading.show();

            var isEdit = !!this.crud.form.id
            var result = null
            if(isEdit) {
              result = serviceAPI.editarRemitos(payload);
            } else {
              result = serviceAPI.agregarRemitos(payload);
            }

            result.then((response) => {
              this.modal.form.active = false
              loader.hide()

              if(!isEdit && this.loadInvoiceAfterSave && response.data && response.data.id) {
                this.$router.push({
                  name: 'PurchaseStaffPurchaseGenerateByRemits',
                  params: { remitsID: response.data.id },
                  query: { _t: Date.now() }
                })
                return
              }

              this.filterPurchaseRemits()
              this.$awn.success("Remito generado con éxito");
            })
            .catch(error => {
              loader.hide()
              this.$awn.alert(Error.showError(error));
            })
          }
        })
        .catch(error => {
          this.$awn.alert(Error.showError(error));
        })              
      },  
      cancel() {
        this.confirmDialog('¿Desea cancelar el remito de compra?', {
          title: 'Cancelar el Remito',
          danger: true,
          okTitle: 'SI',
          cancelTitle: 'NO'
        })
        .then(value => {
          if (value) {        
            this.modal.form.active = false
          }
        })
        .catch(error => {
          this.$awn.alert(Error.showError(error));
        })   
      },

      // ABM VALIDACIONES
      purchaseRemitsValidDetailGeneral() {               
        if( this.crud.form.suppliers_id && 
            this.crud.form.points_sales_id && 
            this.crud.form.types_vouchers_id && 
            this.crud.form.letter && 
            this.crud.form.point_sale && 
            this.crud.form.number) {          
          return true;
        } else {
          if(!this.crud.form.suppliers_id) {
            this.$awn.alert("No se cargó el proveedor");
            return false;
          }            

          if(!this.crud.form.points_sales_id) {
            this.$awn.alert("No se cargó el punto de venta de imputación");
            return false;
          }            

          if( !this.crud.form.types_vouchers_id ||
              !this.crud.form.letter || 
              !this.crud.form.point_sale ||
              !this.crud.form.number ) {                
            this.$awn.alert("Faltan datos del comprobante");
            return false;         
          }                       
        }        
      },
      purchaseRemitsValidDetailPurchaseOrder() {
        if(this.crud.form.source_mode != 'from_order') {
          return true
        }

        if(!this.crud.form.purchase_orders_id) {
          this.$awn.alert('Debe seleccionar una orden de compra pendiente')
          return false
        }

        if(this.purchaseOrderGridItems.length) {
          this.cachePurchaseOrderZeroDetails()
          this.forceRender()
          return true
        }

        this.$awn.alert('Debe cargar al menos un ítem con cantidad mayor a cero')
        return false
      },
      purchaseRemitsValidDetailItems() {
        if(!this.detailProductsForDetailTab.length && !this.detailProductsCompoundForDetailTab.length) {
          this.$awn.alert('No se cargó ningún ítem')
          return false
        }
        var unlinked = (this.crud.form.detailProducts || []).filter(item => item._supplierNotLinked)
        if(unlinked.length) {
          var names = unlinked.map(item => (item.product && item.product.name) ? item.product.name : 'Producto').join(', ')
          this.$awn.alert('Los siguientes productos no tienen al proveedor vinculado en costos: ' + names + '. Vinculalos desde el ícono de edición de precio antes de continuar.')
          return false
        }
        return true
      },
      setDetailItems(event, type) {
        // Mantener la misma referencia de objeto para que actualizaciones async
        // de price_cost (hidratación) se reflejen en el payload al guardar
        var detail = Array.isArray(event) ? event.map((element) => {
          element.location_id = element.location ? element.location.id : 0
          return element
        }) : []

        if(this.crud.form.source_mode == 'from_order') {
          var cache = type=="products" ? this.purchaseOrderZeroCache.products : this.purchaseOrderZeroCache.productsCompound
          var current = type=="products" ? this.crud.form.detailProducts : this.crud.form.detailProductsCompound
          var zeroItems = Array.isArray(cache) && cache.length
            ? cache
            : (current || []).filter((item) => parseFloat(item.quantity || 0) <= 0)

          var detailKeys = {}
          detail.forEach((item) => {
            detailKeys[this.buildPurchaseOrderDetailKey(item, type)] = true
          })

          zeroItems.forEach((item) => {
            var itemCopy = Object.assign({}, item)
            itemCopy.location_id = itemCopy.location ? itemCopy.location.id : (itemCopy.location_id || 0)
            var key = this.buildPurchaseOrderDetailKey(itemCopy, type)
            if(!detailKeys[key]) {
              detail.push(itemCopy)
              detailKeys[key] = true
            }
          })
        }

        if(type=="products") {
          this.crud.form.detailProducts = detail                
        } 
        if(type=="productsCompound") {          
          this.crud.form.detailProductsCompound = detail                
        }              
      },   
      getPurchaseOrderGridItemCode(item) {
        if(item.productCompound && item.productCompound.code) {
          return item.productCompound.code
        }
        if(item.product && item.product.code) {
          return item.product.code
        }
        return '--'
      },
      getPurchaseOrderGridItemName(item) {
        if(item.productCompound && item.productCompound.name) {
          return item.productCompound.name
        }
        if(item.product && item.product.name) {
          var label = item.product.name
          var colorName = item.product.color && item.product.color.name ? item.product.color.name : ''
          var waistName = item.product.waist && item.product.waist.name ? item.product.waist.name : ''
          if(colorName && waistName) {
            label += ' (' + colorName + ' - ' + waistName + ')'
          } else if(colorName) {
            label += ' (' + colorName + ')'
          } else if(waistName) {
            label += ' (' + waistName + ')'
          }
          return label
        }
        return 'Sin descripción'
      },
      getPurchaseOrderGridItemUnit(item) {
        if(item.unit_measurement) {
          if(item.unit_measurement.reference) {
            return item.unit_measurement.reference
          }
          if(item.unit_measurement.name) {
            return item.unit_measurement.name
          }
        }
        return ''
      },
      buildPurchaseOrderLocationLabel(location) {
        if(!location || !location.zone || !location.zone.deposit) {
          return ''
        }
        var depositName = location.zone.deposit.name || location.zone.deposit.reference || ''
        if(location.full_reference && location.full_reference == depositName) {
          return depositName
        }
        return [depositName, location.zone.reference || location.zone.name, location.reference || location.name]
          .filter(Boolean)
          .join(' / ')
      },
      loadAvailableLocations(force=false) {
        if(this.arr.purchaseOrderLocations.length && !force) {
          return Promise.resolve(this.arr.purchaseOrderLocations)
        }

        return serviceAPI.obtenerUbicacionesDeposito({
          filter: '',
          where: [{ field: 'active', condition: true }],
          orWhere: [],
        })
        .then((response) => {
          var rows = Array.isArray(response.data) ? response.data : []
          this.arr.purchaseOrderLocations = rows
          this.arr.purchaseOrderLocationOptions = rows.map((location) => {
            return {
              value: location.id,
              text: this.buildPurchaseOrderLocationLabel(location),
            }
          })
          this.applyDefaultLocationToCurrentDetails()
          return rows
        })
      },
      applyDefaultLocationToCurrentDetails() {
        var defaultLocation = this.getDefaultPurchaseOrderLocation()
        if(!defaultLocation) {
          return
        }

        this.crud.form.detailProducts.forEach((item) => {
          if(!item.location || !item.location.id) {
            item.location = defaultLocation
            item.location_id = defaultLocation.id
          }
        })
        this.crud.form.detailProductsCompound.forEach((item) => {
          if(!item.location || !item.location.id) {
            item.location = defaultLocation
            item.location_id = defaultLocation.id
          }
        })
      },
      getDefaultPurchaseOrderLocation() {
        if(this.arr.purchaseOrderLocations && this.arr.purchaseOrderLocations.length) {
          return this.arr.purchaseOrderLocations[0]
        }
        return null
      },
      resolvePurchaseOrderItemLocation(location) {
        if(location && location.id) {
          var found = this.arr.purchaseOrderLocations.find((element) => parseInt(element.id) === parseInt(location.id))
          return found || location
        }
        return this.getDefaultPurchaseOrderLocation()
      },
      buildPurchaseOrderDetailKey(item, type='products') {
        var purchaseOrderDetailID = parseInt(item.purchase_orders_detail_id || 0)
        var locationID = parseInt(item.location_id || (item.location && item.location.id ? item.location.id : 0))
        var productID = 0

        if(type == 'productsCompound') {
          productID = parseInt(item.productCompound && item.productCompound.id ? item.productCompound.id : 0)
        } else {
          productID = parseInt(item.product && item.product.id ? item.product.id : 0)
        }

        return purchaseOrderDetailID + '|' + productID + '|' + locationID
      },
      cachePurchaseOrderZeroDetails() {
        this.purchaseOrderZeroCache.products = (this.crud.form.detailProducts || [])
          .filter((item) => parseFloat(item.quantity || 0) <= 0)
          .map((item) => Object.assign({}, item))

        this.purchaseOrderZeroCache.productsCompound = (this.crud.form.detailProductsCompound || [])
          .filter((item) => parseFloat(item.quantity || 0) <= 0)
          .map((item) => Object.assign({}, item))
      },
      normalizePurchaseOrderDetailQuantity(item) {
        var value = parseFloat(item.quantity)
        if(isNaN(value) || value < 0) {
          value = 0
        }
        item.quantity = value
      },
      getRemitPayloadForSave() {
        var payload = JSON.parse(JSON.stringify(this.crud.form))

        if(this.crud.form.source_mode == 'from_order') {
          payload.detailProducts = (payload.detailProducts || []).filter((item) => parseFloat(item.quantity || 0) > 0)
          payload.detailProductsCompound = (payload.detailProductsCompound || []).filter((item) => parseFloat(item.quantity || 0) > 0)
        }

        return payload
      },
      setPurchaseOrderItemLocation(item, locationId) {
        var location = this.arr.purchaseOrderLocations.find((element) => parseInt(element.id) === parseInt(locationId))
        item.location = location || null
        item.location_id = item.location ? item.location.id : 0
      },
      mapPurchaseOrderDetailItem(element, sourceType='product') {
        var location = this.resolvePurchaseOrderItemLocation(element.location)
        var item = {
          quantity: parseFloat(element.quantity || 0),
          quantityOld: parseFloat(element.quantity || 0),
          unit_measurement: element.unit_measurement,
          location: location,
          location_id: location ? location.id : 0,
          location_destination: element.location_destination,
          purchase_orders_id: element.purchase_orders_id,
          purchase_orders_detail_id: element.purchase_orders_detail_id,
        }

        if(sourceType == 'compound') {
          item.productCompound = element.productCompound || element.products_compound || element.product || null
        } else {
          item.product = element.product || null
        }

        return item
      },
      getPurchaseOrderLabel(item) {
        if(!item) {
          return ''
        }

        var source = item.data ? item.data : item
        var voucher = 'OC #' + source.id
        if(source.status_label) {
          voucher += ' | ' + source.status_label
        }
        if(source.quantity_pending) {
          voucher += ' | Pendiente: ' + parseFloat(source.quantity_pending).toFixed(2)
        }
        return voucher
      },
      loadPendingPurchaseOrders(resetSelection=true) {
        if(!this.crud.form.suppliers_id) {
          this.arr.purchaseOrdersPending = []
          if(resetSelection) {
            this.crud.form.purchase_order = null
            this.crud.form.purchase_orders_id = 0
            this.crud.form.detailProducts = []
            this.crud.form.detailProductsCompound = []
            this.forceRender()
          }
          return false
        }

        let params = {
          suppliers: [{ code: this.crud.form.suppliers_id }],
          status: [{ code: 'pending' }, { code: 'partial' }],
          date_start: moment().subtract(5, 'years').format('YYYY-MM-DD'),
          date_end: moment().add(1, 'day').format('YYYY-MM-DD'),
        }

        serviceAPI.obtenerOrdenesCompraPendientes(params, 1)
        .then((response) => {
          var data = response.data
          var rows = data && data.data ? data.data : []
          this.arr.purchaseOrdersPending = rows
            .filter((element) => parseFloat(element.quantity_pending || 0) > 0)
            .map((element) => {
              return {
                code: element.id,
                label: this.getPurchaseOrderLabel(element),
                data: element,
              }
            })

          if(resetSelection) {
            this.crud.form.purchase_order = null
            this.crud.form.purchase_orders_id = 0
            this.crud.form.detailProducts = []
            this.crud.form.detailProductsCompound = []
            this.forceRender()
          }
        })
        .catch(error => {
          this.$awn.alert(Error.showError(error))
        })
      },
      loadPurchaseOrderToRemit(object) {
        if(!object) {
          this.crud.form.purchase_orders_id = 0
          this.crud.form.purchase_order = null
          this.crud.form.detailProducts = []
          this.crud.form.detailProductsCompound = []
          this.purchaseOrderZeroCache.products = []
          this.purchaseOrderZeroCache.productsCompound = []
          this.forceRender()
          return false
        }

        let loader = this.$loading.show()
        Promise.all([
          this.loadAvailableLocations(),
          serviceAPI.prearmarRemitoDesdeOrden(object.code, {
            date: this.crud.form.date,
            points_sales_id: this.crud.form.points_sales_id,
            types_vouchers_id: this.crud.form.types_vouchers_id,
          }),
        ])
        .then((response) => {
          var data = response[1].data
          this.crud.form.purchase_order = object
          this.crud.form.purchase_orders_id = object.code

          if(data.suppliers_id) {
            this.crud.form.suppliers_id = data.suppliers_id
          }
          if(data.points_sales_id && !this.crud.form.points_sales_id) {
            this.crud.form.points_sales_id = data.points_sales_id
          }
          if(data.types_vouchers_id && !this.crud.form.types_vouchers_id) {
            this.crud.form.types_vouchers_id = data.types_vouchers_id
          }
          if(data.letter && !this.crud.form.letter) {
            this.crud.form.letter = data.letter
          }
          this.crud.form.detailProducts = []
          if(data.detailProducts) {
            data.detailProducts.forEach((element) => {
              this.crud.form.detailProducts.push(this.mapPurchaseOrderDetailItem(element, 'product'))
            })
          }

          this.crud.form.detailProductsCompound = []
          if(data.detailProductsCompound) {
            data.detailProductsCompound.forEach((element) => {
              this.crud.form.detailProductsCompound.push(this.mapPurchaseOrderDetailItem(element, 'compound'))
            })
          }

          this.cachePurchaseOrderZeroDetails()
          this.forceRender()
          loader.hide()
        })
        .catch(error => {
          loader.hide()
          this.$awn.alert(Error.showError(error))
        })
      },
      
      // ABM FLUJO
      loadSuppliers (object) {        
        if(object){
          if(this.crud.form.suppliers_id != object.id) {
            this.forceRender()
          }

          this.crud.form.suppliers = object
          this.crud.form.suppliers_id = object.id   
                    
          if(object.data_fiscal) {
            if(object.data_fiscal.points_sales_id) {
              if(!this.crud.form.points_sales_id) {
                this.crud.form.points_sales = object.data_fiscal.points_sales
                this.crud.form.points_sales_id = object.data_fiscal.points_sales_id
              }
            }          
          }          

          if(this.crud.form.source_mode == 'from_order') {
            this.loadPendingPurchaseOrders(true)
            this.loadAvailableLocations()
          }
        } else {
          this.crud.form.suppliers = null
          this.crud.form.suppliers_id = 0        
          
          this.crud.form.points_sales = null
          this.crud.form.points_sales_id = 0

          this.cleanDataVoucher()
          this.arr.purchaseOrdersPending = []
          this.crud.form.purchase_order = null
          this.crud.form.purchase_orders_id = 0
        }
      },   
      loadPointsSales (object) {                
        if(object){
          this.crud.form.points_sales = object
          this.crud.form.points_sales_id = object.id             
        } else {
          this.crud.form.points_sales = null
          this.crud.form.points_sales_id = 0
        }
      },                
      cleanDataVoucher() {
        this.arr.typeVouchers = null
        this.arr.select.typeVouchers = []

        this.crud.form.letter = ''
        this.crud.form.types_vouchers_id = 0
        this.crud.form.types_vouchers = null 
      },      

      // SELECT VOUCHER 
      searchVoucherBySupplier() {
        if(!this.crud.form.id) {
          if(this.crud.form.suppliers_id && this.crud.form.points_sales_id ) {
            var result = serviceAPI.obtenerVoucherBySupplier({
              'suppliers_id': this.crud.form.suppliers_id,
              'reference': this.crud.form.selectTypeVoucher,
              'point_sale_id': this.crud.form.points_sales_id,
            });
            result.then((response) => {    
              this.crud.form.types_vouchers_id = response.data[0].id							
              this.searchVoucher()                          
            })
            .catch(error => {
              this.$awn.alert(Error.showError(error));
            })   						           
          }
        }
      },			
      searchVoucher() {
        if(!this.crud.form.id) {
          if(this.crud.form.suppliers_id && this.crud.form.points_sales_id ) {
            var result = serviceAPI.obtenerVoucherById({
              'points_sales_id': this.crud.form.points_sales_id,
              'types_vouchers_id' : this.crud.form.types_vouchers_id,
            });
            result.then((response) => {                   
              this.crud.form.types_vouchers = response.data.type_voucher
              this.crud.form.letter = response.data.type_voucher.letter                            
            })
          }
        }
      },        

      // FILTER
      filterLoad() {
        this.filterLoadSuppliers()
        this.filters.date_start = moment().subtract(30, 'days').format('YYYY-MM-DD') 
        this.filters.date_end = moment().format('YYYY-MM-DD') 

        this.restoreFilterStorage()
      },
      filterLoadSuppliers() {
        var result = serviceAPI.obtenerProveedores()        
        result.then((response) => {
          var data = response.data    

          this.arr.filters.suppliers = []
          data.forEach(element => {            
            this.arr.filters.suppliers.push({ code: element.id, label: element.name })            
          });          
        })   
      },       
      filterPurchaseRemits(resetPage=false) {
        if(resetPage) {
          this.table.currentPage = 1
        }

        this.saveFilterStorage()
        this.table.isBusy = true
        var result = serviceAPI.filtrarRemitos(this.filters, this.table.currentPage)        
        result.then((response) => {      
          
          var data = response.data
          this.table.tablePaginate = data

          this.table.items = data.data
          this.arr.purchaseRemits = data.data
          this.prepareExport(data.data)
          
          if(this.itemSelected) {
            this.table.items.forEach(element => {
              if(element.id == this.itemSelected.id) {
                this.openSub(element)
              }
            })
          }

          if(this.table.rowSelected!=null) {            
            this.table.items[this.table.rowSelected]._showDetails = true                   
          }         

          if(this.$refs.table) {            
            this.$refs.table.$forceUpdate()
          }          
          
          this.table.isBusy = false
        })                         
        .catch(error => {                 
          this.table.isBusy = false  
          this.$awn.alert(ErrorToken.valid(error));
        })  
      },        

      // FILTROS SAVE
      saveFilterStorage() {
        Storage.setValue('filter_crud_purchase_remits', JSON.stringify(this.filters))
      },
      restoreFilterStorage() {
        if(Storage.getValue('filter_crud_purchase_remits')) {
          this.filters = JSON.parse(Storage.getValue('filter_crud_purchase_remits'))
        }         
      },             
      
      // COMPROBANTE DETAIL
      openSub(item) {                
        this.itemSelected = item        
        
        this.tableSub.items = item.details      
        this.detailDrawer = true
      },    
      hideSub() {
        this.tableSub.items=[]        
        this.itemSelected = null
        this.detailDrawer = false
      },     
      openDetail() {              
        this.tableSub.items = this.itemSelected.details            
        this.tableDetail.items = this.itemSelected.details            
       
        this.modal.detail._id = Math.random().toString(36).slice(2, 8)
        this.modal.detail.title = "Detalle del Comprobante"
        this.modal.detail.active = true

        let loader = this.$loading.show();
        serviceAPI.mostrarRemitos(this.itemSelected.id)
        .then((response) => {  
          var data = response.data
          if(data && data.details) {
            this.tableDetail.items = this.getRemitDetailsFlat(data.details)
          }
          loader.hide()
        })
        .catch(() => {
          loader.hide()
        })
      },        
      getDetailCode(item) {        
        if(item.products_id || item.products_colors_id || item.products_waist_id || item.products_color_waist_id) {
          return this.getProductCode(item)
        }            
        if(item.products_compound_id) {          
          return item.products_compound.code
        }                
      },
      getDetailName(item) {        
        if(item.products_id || item.products_colors_id || item.products_waist_id || item.products_color_waist_id) {
          return this.getProductName(item)
        }               
        if(item.products_compound_id) {          
          return item.products_compound.name
        }                
      },       
      getProductCode(item) {
        var code = ''
        var prod = null

        if(item.products_code) {
          code = item.products_code
        }          

        if(item.products_id) {
          if(code) {
            return code
          } else {                      
            return item.product.code
          }
        }

        if(item.products_colors_id) {
          prod = item.products_color 
          if(code) {
            return code
          } else {
            return prod.code
          }           
        }

        if(item.products_waist_id) {
          prod = item.products_waist 
          if(code) {
            return code
          } else {
            return prod.code
          }                     
        }

        if(item.products_color_waist_id) {
          prod = item.products_color_waist 
          if(code) {
            return code
          } else {
            return prod.code
          }                     
        }
      },      
      getProductName(item) {
        var name = ''
        var prod = null

        if(item.products_name) {
          name = item.products_name
        }          
        
        if(item.products_id) {
          if(name) {
            return name
          } else {
            return item.product.name
          }          
        }

        if(item.products_colors_id) {
          prod = item.products_color 
          if(name) {
            return name + ' (' + prod.color.name + ')'
          } else {
            return prod.name + ' (' + prod.color.name + ')'
          }                    
        }

        if(item.products_waist_id) {
          prod = item.products_waist           
          if(name) {
            return name + ' (' + prod.waist.name + ')'
          } else {
            return prod.name + ' (' + prod.waist.name + ')'
          }
        }

        if(item.products_color_waist_id) {
          prod = item.products_color_waist 
          if(name) {
            return name + ' (' + prod.color.name + ' - ' + prod.waist.name + ')'
          } else {
            return prod.name + ' (' + prod.color.name + ' - ' + prod.waist.name + ')'
          }          
        }
      },  
      
      // PRINT
      sendPrinter(item) {            
        if(item.deposit_operation) {
          this.crud.print.key=Math.random()
          this.crud.print.id=item.deposit_operation.id
          this.crud.print.reference = item.deposit_operation.reportDefault
        } else {
         this.$awn.alert("No se encontró el movimiento de depósito");
         return false 
        }
      },     

      // PROCESS ADMINISTRATIVE      
      generateInvoice(item) {
        if(!this.hasRemitPrices(item)) {
          this.$awn.alert('El remito tiene ítems sin precio de costo. Editalo para completar los precios antes de generar la factura.')
          return
        }
        this.$router.push({ name: 'PurchaseStaffPurchaseGenerateByRemits', params: { remitsID: item.id }, query: { _t: Date.now() } })
      },      
      isRemitLinked(item) {
        if(!item || !item.details || !item.details.length) {
          return false
        }
        var linked = true
        item.details.forEach(element => {
          if(!element.purchase_id) {
            linked = false
          }
        })
        return linked
      },
      hasRemitPrices(item) {
        if(!item || !item.details || !item.details.length) return true
        return item.details.every(element => parseFloat(element.price_cost) > 0)
      },
      hasRemitLinkedItems(item) {
        if(!item || !item.details || !item.details.length) {
          return false
        }
        var linked = false
        item.details.forEach(element => {
          if(element.purchase_id) {
            linked = true
          }
        })
        return linked
      },
      isRemitItemLinked(item) {
        return item && item.purchase_id ? true : false
      },
      isRemitPartiallyLinked(item) {
        if(!item || !item.details || !item.details.length) {
          return false
        }
        return this.hasRemitLinkedItems(item) && !this.isRemitLinked(item)
      },
      getRemitBillingStatusLabel(item) {
        if(!item || !item.details || !item.details.length) {
          return 'Sin items'
        }
        if(!this.hasRemitPrices(item)) {
          return 'Sin precios'
        }
        if(this.isRemitLinked(item)) {
          return 'Factura vinculada'
        }
        if(this.isRemitPartiallyLinked(item)) {
          return 'Parcial'
        }
        return 'Pendiente'
      },
      getPurchaseReference(purchase) {
        if(purchase && purchase.type_voucher && purchase.point_sale) {
          return purchase.type_voucher.name + ' ' + purchase.point_sale.toString().padStart(4,'0') + '-' + purchase.number.toString().padStart(8,'0')
        }
        if(purchase && purchase.id) {
          return 'ID #' + purchase.id
        }
        return ''
      },
      getRemitPurchaseDifferences(remit) {
        const groups = {}
        const details = remit && remit.details ? remit.details : []

        details.forEach(detail => {
          if(!detail.purchase_id || !detail.purchase) {
            return
          }
          if(!groups[detail.purchase_id]) {
            groups[detail.purchase_id] = {
              purchase: detail.purchase,
              remitAmount: 0,
            }
          }
          const detailAmount = parseFloat(detail.quantity) * parseFloat(detail.price_cost || 0)
          if(!isNaN(detailAmount)) {
            groups[detail.purchase_id].remitAmount += detailAmount
          }
        })

        return Object.keys(groups)
          .map(key => ({
            purchase: groups[key].purchase,
            remitAmount: groups[key].remitAmount,
            invoiceAmount: parseFloat(groups[key].purchase.amount_total) || 0,
          }))
          .filter(difference => Math.abs(difference.invoiceAmount - difference.remitAmount) > 0.009)
      },
      formatRemitPurchaseAmount(amount) {
        return Intl.NumberFormat('es-AR', { style: 'currency', currency: this.getCurrency || 'ARS' }).format(amount)
      },
      getRemitDetailsFlat(details) {
        var items = []
        if(details && details.products) {
          details.products.forEach(element => {
            items.push(element)
          })
        }
        if(details && details.products_compound) {
          details.products_compound.forEach(element => {
            items.push(element)
          })
        }
        return items
      },
      canShowActions(item) {
        if(!item) {
          return false
        }
        if(!this.hasRemitLinkedItems(item)) {
          return true
        }
        return false
      },
    }    
   
  }
</script>

<style>
  .table-full-detail-purchase-remits {
    overflow: auto;
    max-height: 350px;    
  }    
  .crud-purchase-remits-price-title {
    font-size: 15px;
    font-weight: 600;
  }
  .crud-purchase-remits-price-code {
    color: gray;
  }
  .purchase-remits-price-cost-divider {
    border-left: 1px solid #c7c7c7;
    text-align: end;        
    padding-top: 5px;
  }  
  .crud-purchase-remits-title-table-price {
    color: #2f353a;
    font-weight: 700;
    padding-right: 10px;
    width: 30%;
    text-align: right
  }  
  .crud-purchase-remits-value-table-price {
    padding-left: 5px;
    text-align: right
  }    
  .purchase-remits-price-cost-update {
    text-align: end; 
    padding-top: 5px;
  }
  .purchase-remits-list-price-cost {
    overflow: auto;
    max-height: 350px;
  }
  .purchase-remits-price-cost-not-related {
    font-size: 10px;
    color: red;
  }
  .purchase-remits-detail-item {
    white-space: normal;
    line-height: 1.2;
  }
  .purchase-remits-detail-item__code {
    color: var(--app-color-text-muted);
    font-size: 12px;
    font-weight: var(--app-font-semibold);
    line-height: 1.15;
  }
  .purchase-remits-detail-item__name {
    color: var(--app-color-text);
    font-weight: var(--app-font-semibold);
    line-height: 1.2;
  }
  .purchase-remits-detail-location {
    white-space: normal;
    line-height: 1.2;
  }
  .table-purchase-remits-from-order {
    overflow: auto;
    max-height: 320px;
  }
  .purchase-remits-table-footer {
    padding-right: 12px;
  }
  .btn.purchase-remits-action-toggle-labeled {
    display: inline-flex !important;
    align-items: center;
    justify-content: flex-start;
    width: auto !important;
    height: auto !important;
    min-height: 0;
    padding: 0 !important;
    border: none !important;
    background: transparent !important;
    box-shadow: none !important;
    color: var(--app-color-text-secondary) !important;
    font-size: 13px;
    line-height: 1.15;
  }
  .btn.purchase-remits-action-toggle-labeled:hover,
  .btn.purchase-remits-action-toggle-labeled:focus {
    color: var(--app-color-primary) !important;
    text-decoration: underline;
  }
  .purchase-remits-form-modal-dialog {
    max-width: min(1400px, 95vw);
  }
  .purchase-remits-form-section {
    border: 1px solid var(--app-color-border);
    border-radius: 8px;
    background: var(--app-color-surface);
    padding: 14px;
  }
  .purchase-remits-form-section__title {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 12px;
    color: var(--app-color-text);
    font-size: var(--app-text-sm);
    font-weight: var(--app-font-semibold);
  }
  .purchase-remits-voucher-type {
    display: flex;
    align-items: center;
    gap: 8px;
    min-height: 31px;
    padding: 5px 9px;
    border: 1px solid #a6d8e8;
    border-radius: 4px;
    background: #edf9fc;
    color: #14566e;
    line-height: 1.05;
  }
  .purchase-remits-voucher-type__icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: #d3f0f8;
    color: #08759a;
  }
  .purchase-remits-voucher-type strong,
  .purchase-remits-voucher-type small {
    display: block;
  }
  .purchase-remits-voucher-type strong {
    font-size: 0.78rem;
    font-weight: var(--app-font-semibold);
    text-transform: uppercase;
    letter-spacing: 0.02em;
  }
  .purchase-remits-voucher-type small {
    margin-top: 2px;
    color: #4b7b8d;
    font-size: 0.68rem;
  }
  .crud-sales-wizard .wizard-card-footer {
    display: flex !important;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 14px 4px 0;
  }
  .crud-sales-wizard .wizard-card-footer .wizard-footer-left,
  .crud-sales-wizard .wizard-card-footer .wizard-footer-right {
    float: none !important;
  }
  .crud-sales-wizard .wizard-card-footer .wizard-footer-right {
    margin-left: auto;
  }
  .crud-sales-wizard .wizard-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 112px;
    height: 36px;
    padding: 6px 14px;
    border-width: 1px;
    border-radius: 6px;
    font-size: 13px;
    font-weight: var(--app-font-semibold);
    box-shadow: none;
    transition: background-color .15s ease, border-color .15s ease, color .15s ease;
  }
  .crud-sales-wizard .wizard-footer-left .wizard-btn::before,
  .crud-sales-wizard .wizard-footer-right .wizard-btn::after {
    font-family: 'Material Symbols Rounded';
    font-size: 18px;
    font-weight: normal;
    font-style: normal;
    font-feature-settings: 'liga';
    line-height: 1;
  }
  .crud-sales-wizard .wizard-footer-left .wizard-btn::before {
    content: 'arrow_back';
    margin-right: 6px;
  }
  .crud-sales-wizard .wizard-footer-right .wizard-btn::after {
    content: 'arrow_forward';
    margin-left: 6px;
  }
  .crud-sales-wizard .wizard-footer-left .wizard-btn {
    background-color: var(--app-color-surface) !important;
    border-color: var(--app-color-border-strong) !important;
    color: var(--app-color-text-secondary) !important;
  }
  .crud-sales-wizard .wizard-footer-left .wizard-btn:hover {
    background-color: var(--app-color-surface-subtle) !important;
    color: var(--app-color-text) !important;
  }
  .crud-sales-wizard .wizard-footer-right .wizard-btn {
    background-color: var(--app-color-primary) !important;
    border-color: var(--app-color-primary) !important;
    color: #fff !important;
  }
  .crud-sales-wizard .wizard-footer-right .wizard-btn:hover {
    filter: brightness(.94);
  }
  @media (max-width: 767.98px) {
    .crud-sales-wizard .wizard-card-footer {
      gap: 8px;
      padding: 12px 0 0;
    }
    .crud-sales-wizard .wizard-footer-left,
    .crud-sales-wizard .wizard-footer-right {
      display: flex;
      flex: 1 1 0;
    }
    .crud-sales-wizard .wizard-card-footer .wizard-footer-right {
      margin-left: 0;
    }
    .crud-sales-wizard .wizard-footer-left > span,
    .crud-sales-wizard .wizard-footer-right > span {
      display: block;
      width: 100%;
    }
    .crud-sales-wizard .wizard-btn {
      width: 100%;
      min-width: 0;
    }
    .table-purchase-remits-custom.app-table.b-table-stacked-md > tbody > tr > td:last-child::before {
      content: attr(data-label) !important;
    }
  }
</style>
